/*
 * @Author: mina.min
 * @Date: 2025-01-20 10:00:00
 * @LastEditors: mina.min
 * @LastEditTime: 2025-09-04 16:01:24
 * @FilePath: /OTA-frontend/src/pages/FirmwareManagement/FirmwareList.tsx
 * @Description: 小包列表组件
 */
import {
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  type UploadFile,
  type UploadProps,
} from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  addFirmware,
  calculateFileHash,
  completeMultipartUpload,
  completeMultipartUploadEdit,
  deleteFirmware,
  editFirmware,
  getFirmwareDetail,
  getFirmwareList,
  initMultipartUpload,
  sortFirmware,
  uploadFileChunks,
  type FirmwareItem,
  type SortItem,
} from '../../services/firmware'
import { getModuleNameList, type ModuleNameItem } from '../../services/module'
import { getProjectPhaseNameList, type ProjectPhaseItem } from '../../services/projectPhase'
import { formatToCST } from '../../utils'
import './FirmwareList.scss'

const { Title } = Typography
const { Option } = Select
const { TextArea } = Input

// 拖拽类型
const DND_TYPE = 'row'

// 固件列表拖拽行，完全复刻OTAUpgradeDraggableRow
const DraggableRow: React.FC<{
  index: number
  record: FirmwareItem
  moveRow: (dragIndex: number, hoverIndex: number) => void
  onDragStart: () => void
  onDragEnd: () => void
  children: React.ReactNode
}> = ({ index, record, moveRow, onDragStart, onDragEnd, children }) => {
  const ref = React.useRef<HTMLTableRowElement>(null)
  const dragStartRef = React.useRef(false)

  // 查找最近的可滚动祖先节点
  const getScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
    let current: HTMLElement | null = element
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current)
      const overflowY = style.overflowY
      const isScrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        current.scrollHeight > current.clientHeight
      if (isScrollable) return current
      current = current.parentElement
    }
    return null
  }

  // 兼容滚动：优先滚动容器，找不到则滚动页面
  const scrollByY = (delta: number) => {
    const container = getScrollableAncestor(ref.current)
    if (container) {
      container.scrollTop += delta
    } else {
      const scroller = document.scrollingElement || document.documentElement || document.body
      scroller.scrollTop += delta
    }
  }

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_TYPE,
    item: { index, record },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    options: { dropEffect: 'move' },
    end: () => {
      // 兼容不同浏览器：无论是否成功放下都重置拖拽状态
      onDragEnd()
    },
  })

  // 监听拖拽状态变化
  React.useEffect(() => {
    if (isDragging && !dragStartRef.current) {
      // 开始拖拽
      dragStartRef.current = true
      onDragStart()
    } else if (!isDragging && dragStartRef.current) {
      // 结束拖拽
      dragStartRef.current = false
    }
  }, [isDragging, onDragStart])

  const [{ isOver }, drop] = useDrop({
    accept: DND_TYPE,
    collect: (monitor: any) => ({ isOver: monitor.isOver({ shallow: true }) }),
    hover: (item: { index: number; record: FirmwareItem }, monitor: any) => {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      const threshold = hoverMiddleY * 0.3
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY - threshold) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY + threshold) return

      // 自动滚动：当鼠标接近窗口顶部/底部时滚动页面
      const edge = 80
      const viewportHeight = window.innerHeight
      const y = clientOffset.y
      let scrollDelta = 0
      if (y < edge) {
        scrollDelta = Math.max(-20, -((edge - y) / edge) * 20)
      } else if (y > viewportHeight - edge) {
        scrollDelta = Math.min(20, ((y - (viewportHeight - edge)) / edge) * 20)
      }
      if (scrollDelta !== 0) {
        scrollByY(scrollDelta)
      }

      moveRow(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
    drop: (_item, monitor) => {
      if (monitor.didDrop()) {
        // 延迟调用 onDragEnd，确保 moveRow 已经完成
        setTimeout(() => {
          onDragEnd()
        }, 10)
      }
    },
  })
  drag(drop(ref))
  // 使用默认预览，通过CSS约束
  preview(ref)

  if (isDragging) {
    return (
      <tr className='drag-placeholder'>
        <td
          colSpan={100}
          style={{
            height: '60px',
            background: 'linear-gradient(90deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
            border: '2px dashed #3b82f6',
            borderRadius: '8px',
            position: 'relative',
            animation: 'pulse 2s infinite',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            拖拽到此处放置
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr
      ref={ref}
      className={`${isOver ? 'drop-target-row' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {children}
    </tr>
  )
}

const FirmwareList: React.FC = () => {
  const [firmwareList, setFirmwareList] = useState<FirmwareItem[]>([])
  const [filteredFirmwareList, setFilteredFirmwareList] = useState<FirmwareItem[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingFirmware, setEditingFirmware] = useState<FirmwareItem | null>(null)
  const [loading, setLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null)
  const [form] = Form.useForm()
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [uploadStatus, setUploadStatus] = useState<string>('')
  const [uploadLogs, setUploadLogs] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState<boolean>(false) // 添加上传状态保护
  const uploadInProgressRef = useRef<boolean>(false) // 使用ref防止重复上传
  const currentFileRef = useRef<File | null>(null) // 同步跟踪当前选择的文件

  // 拖拽全局滚动兼容（Windows Chrome 在拖拽中滚轮不生效）
  const [isAnyRowDragging, setIsAnyRowDragging] = useState(false)
  useEffect(() => {
    const isWindows = navigator.userAgent.includes('Windows')
    if (!isAnyRowDragging || !isWindows) return
    const onWheel = (e: WheelEvent) => {
      const tableBody = document.querySelector('.ant-table-body') as HTMLElement | null
      let handled = false
      if (tableBody && tableBody.scrollHeight > tableBody.clientHeight) {
        tableBody.scrollTop += e.deltaY
        handled = true
      } else {
        const scroller = document.scrollingElement || document.documentElement || document.body
        const before = scroller.scrollTop
        scroller.scrollTop += e.deltaY
        handled = scroller.scrollTop !== before
      }
      if (handled) {
        e.preventDefault()
      }
    }
    window.addEventListener('wheel', onWheel, { passive: false })
    const reset = () => setIsAnyRowDragging(false)
    window.addEventListener('dragend', reset)
    window.addEventListener('drop', reset)
    window.addEventListener('keyup', e => {
      if (e.key === 'Escape') reset()
    })
    return () => {
      window.removeEventListener('wheel', onWheel as any)
      window.removeEventListener('dragend', reset)
      window.removeEventListener('drop', reset)
    }
  }, [isAnyRowDragging])

  useEffect(() => {
    if (!isAnyRowDragging) return
    const container = document.querySelector('.ant-table-body') as HTMLElement | null
    if (!container) return

    let rafId = 0
    const edge = 80

    const onDragOver = (e: DragEvent) => {
      // Windows 下原生拖拽会吞轮子，这里用指针位置驱动自动滚动
      const rect = container.getBoundingClientRect()
      const y = e.clientY
      let delta = 0
      if (y < rect.top + edge) {
        const t = (rect.top + edge - y) / edge
        delta = -Math.min(24, 24 * t)
      } else if (y > rect.bottom - edge) {
        const t = (y - (rect.bottom - edge)) / edge
        delta = Math.min(24, 24 * t)
      }
      if (delta !== 0) {
        e.preventDefault()
        if (rafId) cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(() => {
          // 优先滚动容器，容器滚不动则滚动页面
          const before = container.scrollTop
          container.scrollTop += delta
          if (container.scrollTop === before) {
            const scroller = document.scrollingElement || document.documentElement || document.body
            scroller.scrollTop += delta
          }
        })
      }
    }

    container.addEventListener('dragover', onDragOver as any, { passive: false })

    const cleanup = () => {
      container.removeEventListener('dragover', onDragOver as any)
      if (rafId) cancelAnimationFrame(rafId)
    }

    return cleanup
  }, [isAnyRowDragging])

  // 搜索和筛选状态
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [filterModuleId, setFilterModuleId] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null)
  const [filterHidden, setFilterHidden] = useState<string>('')

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [total, setTotal] = useState(0)

  const [moduleNameList, setModuleNameList] = useState<ModuleNameItem[]>([])
  const [projectPhaseList, setProjectPhaseList] = useState<ProjectPhaseItem[]>([])

  // 添加防抖定时器引用
  const sortTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isSorting, setIsSorting] = useState(false)
  const [pendingSortData, setPendingSortData] = useState<FirmwareItem[] | null>(null)
  const pendingSortDataRef = useRef(pendingSortData)
  pendingSortDataRef.current = pendingSortData

  // 获取固件列表
  const fetchFirmwareList = async (page = currentPage, size = pageSize) => {
    try {
      setTableLoading(true)
      const params: any = {
        pageNum: page,
        pageSize: size,
      }

      // 添加搜索和筛选参数
      if (searchKeyword.trim()) {
        params.keyword = searchKeyword.trim()
      }
      if (filterModuleId) {
        params.moduleName = filterModuleId
      }
      if (filterStatus) {
        params.status = filterStatus
      }
      if (filterHidden) {
        if (filterHidden === 'hidden') {
          params.hidden = 1
        } else if (filterHidden === 'normal') {
          params.hidden = 0
        }
      }

      const response = await getFirmwareList(params)

      if (response.code === '200') {
        // 处理数据字段映射
        const processedList = response.data.list.map(item => ({
          ...item,
        }))
        setFirmwareList(processedList)
        setTotal(response.data.total || 0)
      } else {
        message.error(response.info)
      }
    } catch (error) {
      message.error('获取固件列表失败')
      console.error('获取固件列表失败:', error)
    } finally {
      setTableLoading(false)
    }
  }

  // 版本号格式化函数
  function formatVersion(num: string | number) {
    if (typeof num === 'number') {
      return num.toString().split('').join('.')
    }
    if (/^\d+$/.test(num)) {
      return num.split('').join('.')
    }
    return num
  }

  // 过滤固件列表
  const filterFirmwareList = () => {
    // 使用后端分页，前端不过滤
    setFilteredFirmwareList(firmwareList)
  }

  // 监听筛选条件变化
  useEffect(() => {
    filterFirmwareList()
  }, [firmwareList])

  // 添加初始化标志 - 使用useRef避免重新渲染
  const isInitialized = useRef(false)

  // 监听筛选条件变化，重新获取数据
  useEffect(() => {
    if (!isInitialized.current) return // 初始化阶段不执行
    // 重置到第一页并重新获取数据
    setCurrentPage(1)
    fetchFirmwareList(1, pageSize)
  }, [filterModuleId, filterStatus, filterHidden])

  // 搜索关键词防抖
  useEffect(() => {
    if (!isInitialized.current) return // 初始化阶段不执行

    // 如果搜索关键词为空，立即搜索
    if (!searchKeyword.trim()) {
      setCurrentPage(1)
      fetchFirmwareList(1, pageSize)
      return
    }

    // 否则进行防抖
    const timer = setTimeout(() => {
      setCurrentPage(1)
      fetchFirmwareList(1, pageSize)
    }, 500) // 500ms 防抖

    return () => clearTimeout(timer)
  }, [searchKeyword])

  // 重置筛选条件
  const resetFilters = () => {
    setSearchKeyword('')
    setFilterModuleId('')
    setFilterStatus('')
    setFilterHidden('')
    setCurrentPage(1)
  }

  // 获取活跃模块列表（从模块管理中获取）
  // const fetchActiveModuleList = async () => {
  //   try {
  //     const response = await moduleApi.getActiveModuleList()
  //     if (response.code === 200) {
  //       setModuleList(response.data)
  //     } else {
  //       message.error('获取模块列表失败：' + response.message)
  //     }
  //   } catch (error) {
  //     message.error('获取模块列表失败')
  //     console.error('获取模块列表失败:', error)
  //   }
  // }

  useEffect(() => {
    // 每次组件挂载时都重置初始化标志
    isInitialized.current = false

    fetchFirmwareList(1, pageSize)
    isInitialized.current = true // 设置初始化完成标志
    // 获取模块名列表
    getModuleNameList()
      .then(data => {
        setModuleNameList(data)
      })
      .catch(error => {
        console.error('获取模块名列表失败:', error)
        message.error('获取模块名列表失败')
      })
    // 获取项目阶段列表
    getProjectPhaseNameList()
      .then(data => {
        setProjectPhaseList(data)
      })
      .catch(error => {
        console.error('获取项目阶段列表失败:', error)
        message.error('获取项目阶段列表失败')
      })
  }, [])

  const handleAdd = () => {
    setEditingFirmware(null)
    setUploadedFile(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEdit = async (record: FirmwareItem) => {
    if (!record.firmwareId) {
      message.error('固件ID不存在，无法编辑')
      return
    }
    try {
      setLoading(true)
      const detail = await getFirmwareDetail(record.firmwareId)
      if (detail) {
        setEditingFirmware(detail)

        // 重置上传相关状态，避免在编辑但未更换文件时出现分片上传的残留状态
        setUploadProgress(0)
        setUploadStatus('')
        setUploadLogs([])
        setIsUploading(false)
        uploadInProgressRef.current = false
        currentFileRef.current = null

        // 处理版本号格式转换
        const formData: any = { ...detail }
        if (typeof detail.version === 'number') {
          const verStr = String(detail.version)
          if (verStr.length > 2) {
            formData.version = verStr.split('').join('.')
          } else {
            formData.version = verStr
          }
        }

        // 处理软件ID格式转换（数字转16进制）
        if (typeof detail.softwareId === 'number') {
          formData.softwareId = convertToHex(detail.softwareId)
        }

        // 为编辑模式创建虚拟文件对象，显示现有文件名
        if (detail.fileName) {
          const virtualFile: UploadFile = {
            uid: '-1',
            name: detail.fileName,
            status: 'done',
            size: detail.fileSize || 0,
            type: 'application/octet-stream',
          }
          setUploadedFile(virtualFile)
        } else {
          setUploadedFile(null)
        }

        form.setFieldsValue(formData)
        // 将 firmwareId 写入表单用于提交时判定编辑模式
        form.setFieldsValue({ firmwareId: detail.firmwareId })
        setIsModalVisible(true)
      } else {
        message.error('获取固件详情失败')
      }
    } catch (error) {
      message.error('获取固件详情失败')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (firmwareId: string) => {
    try {
      setLoading(true)
      const res = await deleteFirmware(firmwareId)
      if (res.code === '200') {
        message.success('删除成功')
        fetchFirmwareList(currentPage, pageSize) // 刷新列表
      } else {
        message.error(res.info || '删除失败')
      }
    } catch (error) {
      console.error('删除固件失败:', error)
      message.error('删除失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  // 防抖的Modal确认处理函数
  const debouncedHandleModalOk = useCallback(async () => {
    if (uploadInProgressRef.current) {
      return
    }
    await handleModalOk()
  }, [])

  const handleModalOk = async () => {
    // 防止重复调用
    if (uploadInProgressRef.current) {
      return
    }

    try {
      const values = await form.validateFields()
      setLoading(true)

      const isEditMode = Boolean(values.firmwareId)
      addUploadLog(`当前提交模式: ${isEditMode ? '编辑' : '新增'}`)

      if (isEditMode) {
        // 检查是否有新文件上传
        const fileToUse = currentFileRef.current || uploadedFile?.originFileObj
        if (fileToUse) {
          const file = fileToUse as File
          const versionNum = Number(String(values.version).replace(/\./g, ''))
          if (isNaN(versionNum)) {
            message.error('版本号格式不正确')
            setLoading(false)
            return
          }

          // 根据文件大小判断上传方式（与新增一致）
          const fileSizeInMB = file.size / (1024 * 1024)
          if (fileSizeInMB > 5) {
            // 分片上传（编辑场景）
            try {
              if (isUploading || uploadInProgressRef.current) {
                message.warning('正在上传中，请稍候...')
                setLoading(false)
                return
              }

              setIsUploading(true)
              uploadInProgressRef.current = true
              setUploadStatus('正在初始化分片上传...')
              setUploadProgress(0)
              setUploadLogs([])

              addUploadLog(
                `开始分片上传(编辑)文件: ${file.name}, 大小: ${fileSizeInMB.toFixed(2)} MB`
              )

              // 初始化分片上传（使用相同 initWithPresign）
              const initResponse = await initMultipartUpload({
                fileName: file.name,
                fileSize: file.size,
                contentType: 'application/octet-stream',
                phaseId: values.phaseId,
                moduleId: values.moduleId,
                version: versionNum,
                chunkSize: 5 * 1024 * 1024,
                expiresInSec: 900,
              })

              if (initResponse.code !== '200') {
                throw new Error(initResponse.info || '初始化分片上传失败')
              }

              const uploadContext = initResponse.data
              setUploadStatus('正在上传文件分片...')
              addUploadLog('开始分片上传(编辑)...')
              const { s3Parts, cosParts } = await uploadFileChunks(
                file,
                uploadContext,
                progress => {
                  setUploadProgress(progress)
                }
              )

              // 计算哈希
              setUploadStatus('正在计算文件哈希...')
              setUploadProgress(90)
              let fileHash: string
              try {
                fileHash = await calculateFileHash(file)
                addUploadLog(`文件哈希计算完成: ${fileHash}`)
              } catch (hashError) {
                console.error('哈希计算失败:', hashError)
                addUploadLog(`哈希计算失败: ${(hashError as Error).message}`, 'warning')
                addUploadLog('继续使用备用哈希值...', 'warning')
                fileHash = `fallback-${file.name}-${file.size}-${Date.now()}`
              }

              // 完成分片上传（编辑场景使用 completeEdit）
              setUploadStatus('正在完成上传...')
              setUploadProgress(95)
              addUploadLog('正在合并文件并完成上传(编辑)...')

              const completeResponse = await completeMultipartUploadEdit({
                // S3/COS 信息
                s3Bucket: uploadContext.s3Bucket,
                objectKey: uploadContext.objectKey,
                s3UploadId: uploadContext.s3UploadId,
                s3Parts: s3Parts,
                cosUploadId: uploadContext.cosUploadId,
                cosParts: cosParts,
                // 元数据
                phaseId: values.phaseId,
                moduleId: values.moduleId,
                softwareId: convertToDecimal(values.softwareId),
                version: versionNum,
                remark: values.remark || '',
                status: values.status,
                hidden: values.hidden,
                fileName: file.name,
                fileSize: file.size,
                fileHash,
                // 编辑特有
                firmwareId: values.firmwareId,
              })

              if (completeResponse.code === '200') {
                setUploadProgress(100)
                setUploadStatus('上传完成')
                addUploadLog('分片上传(编辑)成功！', 'success')
                message.success('编辑分片上传成功！')
              } else {
                throw new Error(completeResponse.info || '编辑分片上传失败')
              }
            } catch (error) {
              console.error('编辑分片上传失败:', error)
              addUploadLog(`编辑分片上传失败: ${(error as Error).message}`, 'error')
              message.error(`编辑分片上传失败: ${(error as Error).message}`)
              setLoading(false)
              return
            } finally {
              setTimeout(() => {
                setUploadProgress(0)
                setUploadStatus('')
                setIsUploading(false)
                uploadInProgressRef.current = false
              }, 2000)
            }
          } else {
            // 小文件：直接表单方式走 edit 接口
            const payload: Parameters<typeof editFirmware>[0] = {
              firmwareId: values.firmwareId,
              phaseId: values.phaseId,
              moduleId: values.moduleId,
              softwareId: convertToDecimal(values.softwareId),
              version: versionNum,
              remark: values.remark,
              status: values.status,
              hidden: values.hidden,
              file: file,
            }

            const res = await editFirmware(payload)
            if (res.code === '200') {
              message.success('编辑成功')
            } else {
              message.error(res.info || '编辑失败')
            }
          }
        } else {
          // 没有新文件，只更新其他信息
          const versionNum = Number(String(values.version).replace(/\./g, ''))
          if (isNaN(versionNum)) {
            message.error('版本号格式不正确')
            setLoading(false)
            return
          }

          const payload: Parameters<typeof editFirmware>[0] = {
            firmwareId: values.firmwareId,
            phaseId: values.phaseId,
            moduleId: values.moduleId,
            softwareId: convertToDecimal(values.softwareId),
            version: versionNum,
            remark: values.remark,
            status: values.status,
            hidden: values.hidden,
          }

          const res = await editFirmware(payload)
          if (res.code === '200') {
            message.success('编辑成功')
          } else {
            message.error(res.info || '编辑失败')
          }
        }
      } else {
        // 新增模式

        // 优先使用ref状态，如果ref有文件就用ref的，否则检查uploadedFile
        const fileToUse = currentFileRef.current || uploadedFile?.originFileObj

        if (!fileToUse) {
          message.error('请上传小包文件')
          setLoading(false)
          return
        }

        const file = fileToUse
        const versionNum = Number(String(values.version).replace(/\./g, ''))
        if (isNaN(versionNum)) {
          message.error('版本号格式不正确')
          setLoading(false)
          return
        }

        // 检查文件大小，如果大于5MB使用分片上传
        const fileSizeInMB = file.size / (1024 * 1024)
        if (fileSizeInMB > 5) {
          // 检查是否正在上传
          if (isUploading || uploadInProgressRef.current) {
            message.warning('正在上传中，请稍候...')
            setLoading(false)
            return
          }

          // 使用分片上传
          try {
            setIsUploading(true) // 设置上传状态
            uploadInProgressRef.current = true // 设置ref状态
            setUploadStatus('正在初始化分片上传...')
            setUploadProgress(0)
            setUploadLogs([]) // 清空日志

            addUploadLog(`开始分片上传文件: ${file.name}, 大小: ${fileSizeInMB.toFixed(2)} MB`)

            // 1. 初始化分片上传
            addUploadLog('正在初始化分片上传并生成预签名URL...')

            // 验证必要参数
            if (!values.phaseId) {
              throw new Error('请选择项目阶段')
            }
            if (!values.moduleId) {
              throw new Error('请选择模块')
            }

            // 验证参数格式
            if (typeof values.phaseId !== 'string' || values.phaseId.trim() === '') {
              throw new Error('项目阶段ID格式不正确')
            }
            if (typeof values.moduleId !== 'string' || values.moduleId.trim() === '') {
              throw new Error('模块ID格式不正确')
            }

            addUploadLog(`阶段ID: ${values.phaseId}`)
            addUploadLog(`模块ID: ${values.moduleId}`)
            addUploadLog(`版本号: ${versionNum}`)
            addUploadLog(`文件名: ${file.name}`)
            addUploadLog(`文件大小: ${file.size} bytes`)

            // 检查模块和阶段是否在有效列表中
            const phaseExists = projectPhaseList.some(p => p.phaseId === values.phaseId)
            const moduleExists = moduleNameList.some(m => m.moduleId === values.moduleId)

            if (!phaseExists) {
              addUploadLog(`警告: 阶段ID ${values.phaseId} 不在有效列表中`, 'warning')
            }
            if (!moduleExists) {
              addUploadLog(`警告: 模块ID ${values.moduleId} 不在有效列表中`, 'warning')
            }

            const initResponse = await initMultipartUpload({
              fileName: file.name,
              fileSize: file.size,
              contentType: 'application/octet-stream',
              phaseId: values.phaseId,
              moduleId: values.moduleId,
              version: versionNum,
              chunkSize: 5 * 1024 * 1024, // 5MB
              expiresInSec: 900,
            })

            if (initResponse.code !== '200') {
              throw new Error(initResponse.info || '初始化分片上传失败')
            }

            const uploadContext = initResponse.data
            addUploadLog(`S3 uploadId: ${uploadContext.s3UploadId}`)
            addUploadLog(`COS uploadId: ${uploadContext.cosUploadId}`)
            addUploadLog(`总分片数: ${uploadContext.totalChunks}`)
            addUploadLog(`分片大小: ${uploadContext.chunkSize} bytes`)
            addUploadLog(`S3 预签名URL数量: ${uploadContext.s3PresignedUrls.length}`)
            addUploadLog(`COS 预签名URL数量: ${uploadContext.cosPresignedUrls.length}`)
            addUploadLog(`对象键: ${uploadContext.objectKey}`)
            addUploadLog(`S3 存储桶: ${uploadContext.s3Bucket}`)
            addUploadLog(`COS 存储桶: ${uploadContext.cosBucket}`)

            // 2. 上传文件分片
            setUploadStatus('正在上传文件分片...')
            addUploadLog('开始分片上传...')
            const { s3Parts, cosParts } = await uploadFileChunks(file, uploadContext, progress => {
              setUploadProgress(progress)
            })

            addUploadLog(
              `所有分片上传完成，S3分片数: ${s3Parts.length}, COS分片数: ${cosParts.length}`
            )

            // 3. 计算文件哈希
            setUploadStatus('正在计算文件哈希...')
            setUploadProgress(90)
            addUploadLog('正在计算文件SHA-256哈希...')

            let fileHash: string
            try {
              fileHash = await calculateFileHash(file)
              addUploadLog(`文件哈希计算完成: ${fileHash}`)
            } catch (hashError) {
              console.error('哈希计算失败:', hashError)
              addUploadLog(`哈希计算失败: ${(hashError as Error).message}`, 'warning')
              addUploadLog('继续使用备用哈希值...', 'warning')
              // 使用备用哈希值
              fileHash = `fallback-${file.name}-${file.size}-${Date.now()}`
            }

            // 4. 完成分片上传
            setUploadStatus('正在完成上传...')
            setUploadProgress(95)
            addUploadLog('正在合并文件并完成上传...')

            // 验证完成请求的参数
            const completeRequestData = {
              // S3 信息
              s3Bucket: uploadContext.s3Bucket,
              objectKey: uploadContext.objectKey,
              s3UploadId: uploadContext.s3UploadId,
              s3Parts: s3Parts,

              // COS 信息
              cosUploadId: uploadContext.cosUploadId,
              cosParts: cosParts,

              // 固件元数据
              phaseId: values.phaseId,
              moduleId: values.moduleId,
              softwareId: convertToDecimal(values.softwareId),
              version: versionNum,
              remark: values.remark || '',
              status: values.status,
              hidden: values.hidden,
              fileName: file.name,
              fileSize: file.size,
              fileHash,
            }

            // 验证必需字段
            const requiredFields = ['phaseId', 'moduleId', 'fileName', 'status']
            for (const field of requiredFields) {
              if (!completeRequestData[field as keyof typeof completeRequestData]) {
                throw new Error(`缺少必需字段: ${field}`)
              }
            }

            addUploadLog(`准备完成请求，S3分片数: ${s3Parts.length}, COS分片数: ${cosParts.length}`)

            const completeResponse = await completeMultipartUpload(completeRequestData)

            if (completeResponse.code === '200') {
              setUploadProgress(100)
              setUploadStatus('上传完成')
              addUploadLog('分片上传成功！', 'success')
              message.success('分片上传成功！')
            } else {
              throw new Error(completeResponse.info || '分片上传失败')
            }
          } catch (error) {
            console.error('分片上传失败:', error)
            addUploadLog(`分片上传失败: ${(error as Error).message}`, 'error')
            message.error(`分片上传失败: ${(error as Error).message}`)
            setLoading(false)
            return
          } finally {
            // 重置上传状态
            setTimeout(() => {
              setUploadProgress(0)
              setUploadStatus('')
              setIsUploading(false) // 重置上传状态保护
              uploadInProgressRef.current = false // 重置ref状态
            }, 2000)
          }
        } else {
          // 使用普通上传
          const res = await addFirmware({
            firmwareId: values.moduleId,
            moduleId: values.moduleId,
            phaseId: values.phaseId,
            softwareId: convertToDecimal(values.softwareId), // 16进制转数字
            version: versionNum,
            remark: values.remark,
            status: values.status,
            hidden: values.hidden,
            file: file,
          })
          if (res.code === '200') {
            message.success('新增成功')
          } else {
            message.error(res.info || '新增失败')
          }
        }
      }

      // 公共的成功后处理
      setIsModalVisible(false)
      form.resetFields()
      setUploadedFile(null)
      fetchFirmwareList(currentPage, pageSize)
    } catch (error) {
      // 统一处理错误
      console.error('操作失败:', error)
      message.error('操作失败，请检查表单或联系管理员')
    } finally {
      setLoading(false)
      // 确保重置上传状态
      setIsUploading(false)
      uploadInProgressRef.current = false
    }
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    setUploadedFile(null)
    setEditingFirmware(null)
    form.resetFields()
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // 16进制转换工具函数
  const convertToHex = (decimal: number): string => {
    if (decimal === 0) return '0x00'
    const hex = decimal.toString(16).toUpperCase()
    return `0x${hex.padStart(2, '0')}`
  }

  const convertToDecimal = (hex: string): number => {
    if (!hex || hex === '0x') return 0
    const cleanHex = hex.replace('0x', '').replace('0X', '')
    return parseInt(cleanHex, 16)
  }

  const validateHex = (hex: string): boolean => {
    if (!hex || hex === '0x') return false
    const pattern = /^0x[0-9A-Fa-f]+$/
    return pattern.test(hex)
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return '草稿'
      case 'PUBLISHED':
        return '已发布'
      default:
        return status
    }
  }

  // 处理文件上传
  const handleFileUpload: UploadProps['onChange'] = info => {
    const { fileList } = info

    if (fileList.length === 0) {
      setUploadedFile(null)
      form.setFieldsValue({ fileName: '' })
      return
    }

    // 只保留最新上传的文件
    const latestFile = fileList[fileList.length - 1]
    setUploadedFile(latestFile)
    // 同步设置ref，优先使用originFileObj，否则使用文件本身
    if (latestFile.originFileObj) {
      currentFileRef.current = latestFile.originFileObj
    } else if (latestFile instanceof File) {
      currentFileRef.current = latestFile
    } else {
      currentFileRef.current = null
    }

    // 自动填充文件名
    form.setFieldsValue({
      fileName: latestFile.name,
    })
  }

  // 文件上传前的验证
  const beforeUpload = (file: any) => {
    // 不限制文件格式，仅校验大小
    const isLe10G = file.size <= 10 * 1024 * 1024 * 1024 // 10GB
    if (!isLe10G) {
      message.error('文件大小不能超过 10GB！')
      return false
    }

    // 关键：即刻写入本地受控的 fileList 与表单字段，防止 onChange 未及时触发导致的空值
    try {
      setUploadedFile(file)
      currentFileRef.current = file // 同步设置ref
      form.setFieldsValue({ fileName: file.name })
    } catch (e) {
      console.warn('[DEBUG] beforeUpload 设置状态失败:', e)
    }

    return false // 阻止自动上传，但允许文件选择
  }

  // Table 组件加 components，完全复刻OTAUpgrade
  const tableComponents = {
    body: {
      row: (props: any) => {
        const index = filteredFirmwareList.findIndex(
          item => String(item.firmwareId) === String(props['data-row-key'])
        )
        const record = filteredFirmwareList[index]
        if (index === -1 || !record) return <tr {...props} />
        return (
          <DraggableRow
            index={index}
            record={record}
            moveRow={moveRow}
            onDragStart={() => setIsAnyRowDragging(true)}
            onDragEnd={handleRowDragEnd}
            {...props}
          />
        )
      },
    },
  }

  // columns 第一列加拖拽图标和序号
  const columns = [
    {
      title: '',
      dataIndex: 'sort',
      width: 60,
      className: 'drag-visible',
      render: (_: any, __: any, idx: number) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className='drag-handle'>
            <HolderOutlined />
          </span>
          <span style={{ marginLeft: 8 }}>{idx + 1}</span>
        </div>
      ),
    },
    {
      title: '模块名称',
      dataIndex: 'moduleName',
      key: 'moduleName',
      width: 150,
    },
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
      width: 120,
      render: (version: any) => formatVersion(version),
    },
    {
      title: '软件ID',
      dataIndex: 'softwareId',
      key: 'softwareId',
      width: 100,
      render: (softwareId: number | null) =>
        typeof softwareId === 'number' ? convertToHex(softwareId) : '-',
    },
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 260,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string, record: FirmwareItem) => (
        <Select
          size='small'
          style={{ width: 120 }}
          value={status}
          onChange={value => handleChangeFirmwareStatus(record, value)}
          disabled={statusUpdatingId === record.firmwareId}
          options={[
            { label: getStatusText('DRAFT'), value: 'DRAFT' },
            { label: getStatusText('PUBLISHED'), value: 'PUBLISHED' },
          ]}
        />
      ),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 180,
      ellipsis: true,
    },
    {
      title: '隐藏版固件',
      dataIndex: 'hidden',
      key: 'hidden',
      width: 110,
      render: (hidden: boolean) => (
        <Tag color={hidden ? 'orange' : 'green'}>{hidden ? '是' : '否'}</Tag>
      ),
    },
    {
      title: '更新者',
      dataIndex: 'createBy',
      key: 'createBy',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (createTime: string) => formatToCST(createTime),
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 160,
      render: (updateTime: string) => formatToCST(updateTime),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: FirmwareItem) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            loading={loading}
          >
            编辑
          </Button>
          <Popconfirm
            title='确定要删除这个小包吗？'
            onConfirm={() => handleDelete(record.firmwareId)}
            okText='确定'
            cancelText='取消'
          >
            <Button type='link' danger icon={<DeleteOutlined />} loading={loading}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // 防抖排序函数
  const debouncedSort = useCallback(
    (newData: FirmwareItem[]) => {
      // 清除之前的定时器
      if (sortTimeoutRef.current) {
        clearTimeout(sortTimeoutRef.current)
      }

      // 设置新的定时器，500ms 后执行排序
      sortTimeoutRef.current = setTimeout(async () => {
        try {
          setIsSorting(true)
          const sortList: SortItem[] = newData.map((item, index) => ({
            firmwareId: item.firmwareId,
            sort: index + 1,
          }))
          const res = await sortFirmware(sortList)
          if (res.code === '200') {
            message.success('排序更新成功')
            // 刷新列表以获取最新的排序
            fetchFirmwareList(currentPage, pageSize)
          } else {
            message.error(res.info || '排序更新失败')
            // 排序失败，恢复原列表
            fetchFirmwareList(currentPage, pageSize)
          }
        } catch (error) {
          console.error('排序更新失败:', error)
          message.error('排序更新失败，请稍后重试')
          // 排序失败，恢复原列表
          fetchFirmwareList(currentPage, pageSize)
        } finally {
          setIsSorting(false)
        }
      }, 500) // 500ms 防抖延迟
    },
    [currentPage, pageSize]
  )

  // 拖拽排序函数（移入组件内部，使用组件内state）
  const moveRow = (dragIndex: number, hoverIndex: number) => {
    const newData = [...filteredFirmwareList]
    const [removed] = newData.splice(dragIndex, 1)
    newData.splice(hoverIndex, 0, removed)
    setFilteredFirmwareList(newData)

    // 保存待排序的数据，但不立即调用接口
    setPendingSortData(newData)
  }

  // 行拖拽结束：触发排序并清空待排序
  const handleRowDragEnd = useCallback(() => {
    const dataToSort = pendingSortDataRef.current
    if (dataToSort) {
      // 使用已实现的防抖排序
      debouncedSort(dataToSort)
      setPendingSortData(null)
    }
    setIsAnyRowDragging(false)
  }, [debouncedSort])

  const handleChangeFirmwareStatus = async (record: FirmwareItem, newStatus: string) => {
    try {
      setStatusUpdatingId(record.firmwareId)
      const response = await editFirmware({ firmwareId: record.firmwareId, status: newStatus })
      if ((response as any).code === '200') {
        message.success('状态更新成功')
        // 本地更新列表中的状态，避免整表刷新
        setFirmwareList(prev =>
          prev.map(item =>
            item.firmwareId === record.firmwareId ? { ...item, status: newStatus } : item
          )
        )
      } else {
        message.error((response as any).info || '状态更新失败')
      }
    } catch (error: any) {
      message.error(error?.message || '状态更新失败')
    } finally {
      setStatusUpdatingId(null)
    }
  }

  // 添加日志记录
  const addUploadLog = (
    message: string,
    type: 'info' | 'success' | 'error' | 'warning' = 'info'
  ) => {
    const timestamp = new Date().toLocaleTimeString()
    const logEntry = `[${timestamp}] [${type.toUpperCase()}] ${message}`
    setUploadLogs(prev => [...prev, logEntry])
  }

  // 操作系统检测（用于区分是否给 Table 设置内部 y 滚动）
  const isWindowsOS = React.useMemo(() => {
    // @ts-ignore
    const uaData = (navigator as any)?.userAgentData
    if (uaData?.platform) return uaData.platform.includes('Windows')
    const ua = navigator.userAgent || ''
    // @ts-ignore
    const pf = (navigator as any).platform || ''
    return /Windows|Win32|Win64/i.test(ua) || /Win/i.test(pf)
  }, [])

  const tableScroll = React.useMemo(() => {
    return isWindowsOS ? { x: 1400 } : { x: 1400, y: 600 }
  }, [isWindowsOS])

  return (
    <DndProvider backend={HTML5Backend}>
      <Spin spinning={loading}>
        <div className='firmware-list'>
          <div className='firmware-list-header'>
            <Title level={4}>小包列表</Title>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
              新增小包
            </Button>
          </div>

          <Card className='firmware-list-card'>
            {/* 拖拽提示 */}
            <div className='drag-tip'>
              <Typography.Text>💡 提示：拖拽列表项可调整顺序</Typography.Text>
              {isSorting && (
                <Typography.Text type='secondary' style={{ marginLeft: 16 }}>
                  🔄 正在保存排序...
                </Typography.Text>
              )}
            </div>
            {/* 搜索和筛选区域 */}
            <div className='search-filter-area'>
              <Form layout='inline'>
                <Form.Item label='关键词搜索'>
                  <Input
                    placeholder='搜索模块名或备注'
                    value={searchKeyword}
                    onChange={e => setSearchKeyword(e.target.value)}
                    allowClear
                    style={{ width: 300 }}
                  />
                </Form.Item>
                <Form.Item label='模块筛选'>
                  <Select
                    placeholder='选择模块'
                    value={filterModuleId}
                    onChange={value => setFilterModuleId(value)}
                    allowClear
                    style={{ width: 150 }}
                  >
                    {moduleNameList.map(module => (
                      <Option key={module.moduleId} value={module.moduleName}>
                        {module.moduleName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label='状态筛选'>
                  <Select
                    placeholder='选择状态'
                    value={filterStatus}
                    onChange={value => setFilterStatus(value)}
                    allowClear
                    style={{ width: 120 }}
                  >
                    <Option value='PUBLISHED'>已发布</Option>
                    <Option value='DRAFT'>草稿</Option>
                  </Select>
                </Form.Item>
                <Form.Item label='小包类型'>
                  <Select
                    placeholder='选择类型'
                    value={filterHidden}
                    onChange={value => setFilterHidden(value)}
                    allowClear
                    style={{ width: 120 }}
                  >
                    <Option value='normal'>普通小包</Option>
                    <Option value='hidden'>隐藏小包</Option>
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Button onClick={resetFilters}>重置</Button>
                </Form.Item>
              </Form>
            </div>

            <Table
              columns={columns}
              dataSource={filteredFirmwareList}
              rowKey='firmwareId'
              components={tableComponents}
              loading={tableLoading}
              scroll={tableScroll}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total: number) => `共 ${total} 条记录`,
                pageSizeOptions: ['50', '100'],
                onChange: (page: number, size: number) => {
                  setCurrentPage(page)
                  setPageSize(size)
                  fetchFirmwareList(page, size)
                },
              }}
            />
          </Card>

          <Modal
            title={editingFirmware ? '编辑小包' : '新增小包'}
            open={isModalVisible}
            onOk={debouncedHandleModalOk}
            onCancel={handleModalCancel}
            width={700}
            okText='确定'
            cancelText='取消'
            confirmLoading={loading}
          >
            <Form
              form={form}
              layout='vertical'
              initialValues={{ status: 'PUBLISHED', hidden: false, softwareId: '0x' }}
            >
              <Form.Item name='firmwareId' hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name='phaseId'
                label='选择阶段'
                rules={[
                  {
                    required: true,
                    message: '请选择阶段',
                  },
                ]}
              >
                <Select placeholder='请选择阶段'>
                  {projectPhaseList.map(phase => (
                    <Option key={phase.phaseId} value={phase.phaseId}>
                      {phase.phaseName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name='moduleId'
                label='选择模块'
                rules={[
                  {
                    required: true,
                    message: '请选择模块',
                  },
                ]}
              >
                <Select placeholder='请选择模块'>
                  {moduleNameList.map(module => (
                    <Select.Option key={module.moduleId} value={module.moduleId}>
                      {module.moduleName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name='version'
                label='版本号'
                rules={[
                  { required: true, message: '请输入版本号' },
                  {
                    pattern: /^\d+(\.\d+)*$/,
                    message: '版本号格式必须为 X.X.X',
                  },
                ]}
              >
                <Input placeholder='请输入版本号，如: 1.0.0' />
              </Form.Item>

              <Form.Item
                name='softwareId'
                label='软件ID (16进制)'
                rules={[
                  { required: true, message: '请输入软件ID' },
                  {
                    validator: (_, value) => {
                      if (!value || value === '0x') {
                        return Promise.reject(new Error('请输入软件ID'))
                      }
                      if (!validateHex(value)) {
                        return Promise.reject(new Error('请输入有效的16进制格式，如: 0x1234'))
                      }
                      const decimal = convertToDecimal(value)
                      if (decimal > 2147483647) {
                        return Promise.reject(new Error('输入值已超出最大限制'))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Input placeholder='请在0x后输入16进制数字，如: 0x1234' />
              </Form.Item>

              <Form.Item
                name='fileName'
                label='小包文件'
                rules={[{ required: true, message: '请选择小包文件' }]}
              >
                <div>
                  <Upload
                    beforeUpload={beforeUpload}
                    onChange={handleFileUpload}
                    fileList={uploadedFile ? [uploadedFile] : []}
                    maxCount={1}
                    onRemove={() => {
                      setUploadedFile(null)
                      currentFileRef.current = null // 清空ref状态
                      form.setFieldsValue({ fileName: '' })
                    }}
                  >
                    <Button icon={<UploadOutlined />}>
                      {editingFirmware ? '替换文件' : '选择文件'}
                    </Button>
                  </Upload>
                  {uploadedFile && (
                    <div style={{ marginTop: 8, color: '#666' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>文件名: {uploadedFile.name}</span>
                        <span>大小: {formatFileSize(uploadedFile.size || 0)}</span>
                      </div>
                    </div>
                  )}

                  {/* 分片上传进度显示 */}
                  {uploadStatus && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ marginBottom: 8, fontSize: '14px', color: '#1890ff' }}>
                        {uploadStatus}
                      </div>
                      <Progress
                        percent={uploadProgress}
                        size='small'
                        status={uploadProgress === 100 ? 'success' : 'active'}
                        strokeColor='#1890ff'
                      />

                      {/* 上传日志显示 */}
                      {uploadLogs.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                            上传日志:
                          </div>
                          <div
                            style={{
                              maxHeight: 200,
                              overflowY: 'auto',
                              backgroundColor: '#f5f5f5',
                              padding: 8,
                              borderRadius: 4,
                              fontSize: '12px',
                              fontFamily: 'monospace',
                              border: '1px solid #d9d9d9',
                            }}
                          >
                            {uploadLogs.map((log, index) => (
                              <div key={index} style={{ marginBottom: 2 }}>
                                {log}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Form.Item>

              <Form.Item
                name='remark'
                label='发布说明'
                rules={[{ required: true, message: '请输入发布说明' }]}
              >
                <TextArea rows={4} placeholder='请输入本次更新的详细说明' />
              </Form.Item>

              <Form.Item
                name='status'
                label='状态'
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Option value='PUBLISHED'>已发布</Option>
                  <Option value='DRAFT'>草稿</Option>
                </Select>
              </Form.Item>

              <Form.Item name='hidden' valuePropName='checked'>
                <Checkbox>上传为隐藏版小包</Checkbox>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </Spin>
    </DndProvider>
  )
}

// 固件列表组件导出
export default FirmwareList

